/**
 * Notification system for Hotelia
 * Handles creating and managing notifications
 */

import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface NotificationData {
  userId: string;
  type: 'group_sponsor' | 'event_invite' | 'friend_request' | 'group_invite';
  title: string;
  message: string;
  status: 'unread' | 'read';
  createdAt?: any;
  readAt?: any;
  // Type-specific data
  groupId?: string;
  eventId?: string;
  fromUserId?: string;
  inviteToken?: string;
  friendRequestId?: string;
}

/**
 * Create a notification for a user
 */
export const createNotification = async (data: NotificationData): Promise<string> => {
  const notificationsRef = collection(db, 'notifications');
  
  // Ensure all required fields are explicitly set (not undefined)
  const notificationData: any = {
    userId: data.userId,
    type: data.type,
    title: data.title,
    message: data.message,
    status: 'unread',
    createdAt: serverTimestamp(),
  };

  // Only add optional fields if they're defined
  if (data.groupId) {
    notificationData.groupId = data.groupId;
  }
  if (data.eventId) {
    notificationData.eventId = data.eventId;
  }
  if (data.fromUserId) {
    notificationData.fromUserId = data.fromUserId;
  }
  if (data.inviteToken) {
    notificationData.inviteToken = data.inviteToken;
  }
  if (data.friendRequestId) {
    notificationData.friendRequestId = data.friendRequestId;
  }

  console.log('🔵 [notifications] Creating notification with data:', notificationData);
  const docRef = await addDoc(notificationsRef, notificationData);
  console.log('🔵 [notifications] Notification created with ID:', docRef.id);
  return docRef.id;
};

/**
 * Create a group sponsor notification
 * Called when someone sponsors a user to join a group
 */
export const createGroupSponsorNotification = async (
  userId: string,
  groupId: string,
  groupName: string,
  sponsorName: string,
  inviteToken: string,
  sponsorId?: string
): Promise<string> => {
  return createNotification({
    userId,
    type: 'group_sponsor',
    title: 'Group Invitation',
    message: `${sponsorName} sponsored you to join "${groupName}"`,
    groupId,
    inviteToken,
    fromUserId: sponsorId,
    status: 'unread',
  });
};

/**
 * Create an event invite notification
 * Called when someone invites a user to join an event
 */
export const createEventInviteNotification = async (
  userId: string,
  eventId: string,
  eventTitle: string,
  inviterName: string,
  inviteToken: string,
  inviterId?: string
): Promise<string> => {
  return createNotification({
    userId,
    type: 'event_invite',
    title: 'Event Invitation',
    message: `${inviterName} invited you to "${eventTitle}"`,
    eventId,
    inviteToken,
    fromUserId: inviterId,
    status: 'unread',
  });
};

/**
 * Create a friend request notification
 * Called when someone sends a friend request
 */
export const createFriendRequestNotification = async (
  toUserId: string,
  fromUserId: string,
  fromUserName: string,
  friendRequestId: string
): Promise<string> => {
  return createNotification({
    userId: toUserId,
    type: 'friend_request',
    title: 'Friend Request',
    message: `${fromUserName} sent you a friend request`,
    fromUserId,
    friendRequestId,
    status: 'unread',
  });
};

/**
 * Get group info for notification
 */
export const getGroupInfo = async (groupId: string) => {
  const groupDoc = await getDoc(doc(db, 'groups', groupId));
  if (groupDoc.exists()) {
    return { id: groupDoc.id, ...groupDoc.data() };
  }
  return null;
};


